import type { AnalysisResponse } from "@patent-studio/api-types";
import { Panel } from "@/components/ui/Panel";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { plural } from "@/lib/plural";

/** What the run checks: its own positions and references, once the worker has stamped them. */
function scopeOf({
  positions,
  references,
}: AnalysisResponse["progress"]): string {
  if (positions === 0) return "Checking your positions against each reference.";
  return `Checking ${plural(positions, "position")} against ${plural(references, "reference")}.`;
}

/**
 * The run in motion, in the attorney's terms. The stream counts cells (a position against a
 * part of a document), a number with no meaning to the attorney, so the page names the
 * positions and references the run covers and says how far it has got.
 */
export function Running({ analysis }: { analysis: AnalysisResponse }) {
  const { done, total } = analysis.progress;
  const percent = total === 0 ? 0 : Math.floor((done / total) * 100);
  return (
    <Panel role="status" className="mt-6">
      <strong className="block text-sm font-bold tracking-tight">
        Running the novelty analysis
      </strong>
      <span className="text-detail text-muted-foreground">
        {`${scopeOf(analysis.progress)} ${String(percent)}% done. The verdicts appear together when every check is done.`}
      </span>
      {/* Hidden from a reader for the reason the spinner is: the words above are inside this
          `role="status"`, and they change only when the percent does, so a named bar in the
          same region would say it a second time. */}
      <ProgressBar
        value={done}
        max={total}
        label="Novelty analysis progress"
        aria-hidden
        className="mt-2"
      />
    </Panel>
  );
}
